/**
 * Financial Analysis Calculator for Solar Energy Systems
 * Now accepts dynamic financial config with fallback to defaults.
 */

export interface FinancialConfig {
  pessimisticRate: number;
  conservativeRate: number;
  optimisticRate: number;
  cdiRate: number;
  energyInflationRate: number;
  panelDegradationPct: number;
}

export const DEFAULT_FINANCIAL_CONFIG: FinancialConfig = {
  pessimisticRate: 0.08,
  conservativeRate: 0.10,
  optimisticRate: 0.12,
  cdiRate: 0.10,
  energyInflationRate: 0.06,
  panelDegradationPct: 0.005,
};

export interface InvestmentScenario {
  label: string;
  annualRate: number;
  results: Record<number, number>;
}

export interface FinancialAnalysis {
  monthlySavings: number;
  annualSavings: number;
  paybackYears: number;
  economy25Years: number;
  projectValue: number;
  currentBill: number;
  estimatedGeneration: number;
  consumption: number;
  savingsPercent: number;
  selfSufficiency: number;
  scenarios: InvestmentScenario[];
  strategies: StrategyComparison[];
  minimumBill: number;
}

export interface StrategyComparison {
  label: string;
  result25Years: number;
  description: string;
}

const PERIODS = [5, 10, 15, 20, 25];

function futureValueWithContributions(
  monthlyContribution: number,
  annualRate: number,
  years: number,
): number {
  const monthlyRate = Math.pow(1 + annualRate, 1 / 12) - 1;
  const months = years * 12;
  if (monthlyRate === 0) return monthlyContribution * months;
  return monthlyContribution * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
}

function futureValueLumpSum(principal: number, annualRate: number, years: number): number {
  return principal * Math.pow(1 + annualRate, years);
}

export function calculateFinancialAnalysis(
  params: {
    monthlySavings: number;
    annualSavings: number;
    paybackYears: number;
    economy25Years: number;
    projectValue: number;
    currentBill: number;
    estimatedGeneration: number;
    consumption: number;
    minimumBill: number;
  },
  financialConfig: FinancialConfig = DEFAULT_FINANCIAL_CONFIG,
): FinancialAnalysis {
  const {
    monthlySavings,
    annualSavings,
    paybackYears,
    economy25Years,
    projectValue,
    currentBill,
    estimatedGeneration,
    consumption,
    minimumBill,
  } = params;

  const savingsPercent = currentBill > 0 ? (monthlySavings / currentBill) * 100 : 0;
  const selfSufficiency =
    consumption > 0 ? Math.min((estimatedGeneration / consumption) * 100, 100) : 0;

  const scenarioConfigs = [
    { label: "Pessimista", annualRate: financialConfig.pessimisticRate },
    { label: "Conservador", annualRate: financialConfig.conservativeRate },
    { label: "Otimista", annualRate: financialConfig.optimisticRate },
  ];

  const scenarios: InvestmentScenario[] = scenarioConfigs.map((config) => {
    const results: Record<number, number> = {};
    PERIODS.forEach((year) => {
      results[year] = Math.round(
        futureValueWithContributions(monthlySavings, config.annualRate, year),
      );
    });
    return {
      label: config.label,
      annualRate: config.annualRate,
      results,
    };
  });

  // Strategy comparison (25 years)
  let totalPaidWithInflation = 0;
  for (let y = 1; y <= 25; y++) {
    totalPaidWithInflation +=
      currentBill * 12 * Math.pow(1 + financialConfig.energyInflationRate, y - 1);
  }

  const cdiInvestment = futureValueLumpSum(projectValue, financialConfig.cdiRate, 25);
  const solarNetResult = economy25Years - projectValue;
  const investedSavings = futureValueWithContributions(
    monthlySavings,
    financialConfig.conservativeRate,
    25,
  );
  const solarPlusInvestment = investedSavings - projectValue;

  const strategies: StrategyComparison[] = [
    {
      label: "Continuar pagando energia",
      result25Years: -Math.round(totalPaidWithInflation),
      description: `Custo total pago à concessionária em 25 anos com inflação energética de ${(financialConfig.energyInflationRate * 100).toFixed(0)}% a.a.`,
    },
    {
      label: "Investir o valor do sistema no CDI",
      result25Years: Math.round(cdiInvestment),
      description: `Investimento único de ${formatBRL(projectValue)} a ${(financialConfig.cdiRate * 100).toFixed(0)}% a.a. por 25 anos.`,
    },
    {
      label: "Instalar energia solar",
      result25Years: Math.round(solarNetResult),
      description: `Economia acumulada em 25 anos (inflação ${(financialConfig.energyInflationRate * 100).toFixed(0)}% a.a.) menos investimento de ${formatBRL(projectValue)}.`,
    },
    {
      label: "Solar + investir a economia mensal",
      result25Years: Math.round(solarPlusInvestment),
      description: `Economia mensal de ${formatBRL(monthlySavings)} investida a ${(financialConfig.conservativeRate * 100).toFixed(0)}% a.a. por 25 anos, menos custo do sistema.`,
    },
  ];

  return {
    monthlySavings,
    annualSavings,
    paybackYears,
    economy25Years,
    projectValue,
    currentBill,
    estimatedGeneration,
    consumption,
    savingsPercent: Math.round(savingsPercent * 10) / 10,
    selfSufficiency: Math.round(selfSufficiency * 10) / 10,
    scenarios,
    strategies,
    minimumBill,
  };
}

export function getPatrimonyChartData(
  scenarios: InvestmentScenario[],
): Array<Record<string, number | string>> {
  return PERIODS.map((year) => {
    const entry: Record<string, number | string> = { year: `${year} anos` };
    scenarios.forEach((s) => {
      entry[s.label] = s.results[year];
    });
    return entry;
  });
}

export function generateInsights(analysis: FinancialAnalysis): string[] {
  const insights: string[] = [];

  insights.push(`Esse sistema não é apenas economia na conta de energia.`);
  insights.push(
    `Ele cria uma renda mensal aproximada de ${formatBRL(analysis.monthlySavings)}.`,
  );

  const conservadorScenario = analysis.scenarios.find((s) => s.label === "Conservador");
  if (conservadorScenario) {
    insights.push(
      `Se essa economia fosse investida ao longo de 25 anos, poderia gerar um patrimônio superior a ${formatBRL(conservadorScenario.results[25])}.`,
    );
  }

  if (analysis.paybackYears > 0) {
    const months = Math.round(analysis.paybackYears * 12);
    insights.push(
      `O retorno do sistema ocorre em aproximadamente ${analysis.paybackYears} anos (${months} meses).`,
    );
  }

  insights.push(
    `A economia acumulada pode ultrapassar ${formatBRL(analysis.economy25Years)} em 25 anos.`,
  );

  return insights;
}

function formatBRL(value: number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

export { PERIODS, formatBRL };
